import React, { useContext, useEffect, useState } from 'react';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import getUserDate from '../../../util/getUserDate';
import ShareOptionsModalV2 from '../../Share/ShareOptionsModalV2/ShareOptionsModalV2';
import ModalErrorV2 from '../Modals/ModalErrorV2';
import { readableFColor } from '../../../util/readableBrandColor';
import StreakCelebrateV2 from './StreakCelebrateV2';
import './Step2CompleteV2.css';

function SparkIcon() {
    return (
        <svg
            className="pv2-s2done__spark-svg"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <path
                fill="currentColor"
                d="M12 1.6l1.55 6.35L20 9.5l-6.45 1.55L12 17.4l-1.55-6.35L4 9.5l6.45-1.55L12 1.6z"
            />
            <path
                fill="currentColor"
                opacity="0.55"
                d="M18.8 14.2l.7 2.15 2.15.7-2.15.7-.7 2.15-.7-2.15-2.15-.7 2.15-.7.7-2.15z"
            />
        </svg>
    );
}

function OolaOneSticker({ fName, fColor, goalText, accent }) {
    const color = accent || fColor || '#c9a227';
    return (
        <div
            className="pv2-s2done__sticker-wrap"
            style={{ ['--pv2-s2-accent']: color }}
        >
            <StreakCelebrateV2 active />
            <div className="pv2-s2done__sticker-glow" aria-hidden="true" />
            <div className="pv2-s2done__sticker-orbit" aria-hidden="true" />
            <div className="pv2-s2done__sticker" role="img" aria-label={`Oola One: ${fName}`}>
                <div className="pv2-s2done__sticker-sheen" aria-hidden="true" />
                <div className="pv2-s2done__sticker-top">
                    <span className="pv2-s2done__sticker-kicker">My Oola One</span>
                    <strong className="pv2-s2done__sticker-f">
                        {(fName || 'Goal').toUpperCase()}
                    </strong>
                </div>
                <div className="pv2-s2done__sticker-bottom">
                    <p className="pv2-s2done__sticker-goal">
                        {goalText || 'Your priority goal for the next 365 days'}
                    </p>
                </div>
            </div>
        </div>
    );
}

/**
 * Portal V2 — Step 2 complete (congrats + Oola One sticker).
 * Classic SelectFinishSection stays on /steps.
 */
export default function Step2CompleteV2({
    AllState,
    handleChangeindexStep,
    mobile,
}) {
    const { checkUserSession } = useContext(AppContext);
    const currUser = checkUserSession() || {};
    const oolaOne = AllState?.SelectedOolaOne || {};
    const areaId =
        oolaOne.areaId != null ? Number(oolaOne.areaId) : null;
    const fMeta =
        (areaId != null ? AllState?.Fs?.[areaId] : null) ||
        AllState?.Fs?.find(
            (x) =>
                Number(x.areaId) === areaId + 1 ||
                Number(x.areaId) === areaId
        ) ||
        {};
    const accent = readableFColor(
        fMeta.name || 'Goal',
        fMeta.color || '#c9a227'
    );

    const [activeShare, setActiveShare] = useState(false);
    const [shareData, setShareData] = useState({});
    const [loadingShare, setLoadingShare] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);

    useEffect(() => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }, []);

    const handleShare = async () => {
        setLoadingShare(true);
        const { error, data } = await requestAxios({
            url: `Customer/GetShareContent/2`,
            token: currUser.token,
        });
        setLoadingShare(false);
        if (error || !data) {
            setErrorOpen(true);
            return;
        }
        setShareData(data);
        setActiveShare(true);
    };

    const startStep3 = () => {
        if (typeof handleChangeindexStep === 'function') {
            handleChangeindexStep(3);
        }
    };

    return (
        <section className="pv2-s2done">
            <div className="pv2-s2done__copy">
                <p className="pv2-s2done__eyebrow">Step 2 · Complete</p>
                <h2 className="pv2-s2done__title">
                    <span>
                        GREAT WORK
                        {currUser.firstName
                            ? ` ${String(currUser.firstName).toUpperCase()}`
                            : ''}
                        !
                    </span>
                    <span className="pv2-s2done__spark" aria-hidden="true">
                        <SparkIcon />
                    </span>
                </h2>
                <p className="pv2-s2done__lead">
                    You completed section two! Take a moment to share your Oola
                    One on your social.
                </p>

                <div className="pv2-s2done__badges" aria-label="Progress">
                    <div className="pv2-s2done__badge">
                        <strong>Top 7</strong>
                        <small>goals set</small>
                    </div>
                    <div className="pv2-s2done__badge">
                        <strong>Oola One</strong>
                        <small>priority locked</small>
                    </div>
                    <div className="pv2-s2done__badge">
                        <strong>Step 3</strong>
                        <small>ready next</small>
                    </div>
                </div>

                <div className="pv2-s2done__actions">
                    <button
                        type="button"
                        className="pv2-s2done__share"
                        onClick={handleShare}
                        disabled={loadingShare}
                    >
                        {loadingShare ? 'Loading…' : 'SHARE!'}
                    </button>
                    <button
                        type="button"
                        className="pv2-s2done__cta"
                        onClick={startStep3}
                    >
                        START STEP 3
                        <span aria-hidden="true">→</span>
                    </button>
                </div>
            </div>

            <div className="pv2-s2done__visual">
                <OolaOneSticker
                    fName={fMeta.name}
                    fColor={fMeta.color}
                    goalText={oolaOne.text}
                    accent={accent}
                />
            </div>

            {mobile ? (
                <div className="pv2-s2done__actions pv2-s2done__actions--mobile">
                    <button
                        type="button"
                        className="pv2-s2done__share"
                        onClick={handleShare}
                        disabled={loadingShare}
                    >
                        {loadingShare ? 'Loading…' : 'SHARE!'}
                    </button>
                    <button
                        type="button"
                        className="pv2-s2done__cta"
                        onClick={startStep3}
                    >
                        START STEP 3
                    </button>
                </div>
            ) : null}

            {activeShare ? (
                <ShareOptionsModalV2
                    webAlias={shareData.webAlias}
                    setActiveShare={setActiveShare}
                    imageUrl={shareData.socialMediaImageUrl}
                    text={`${String(shareData.socialMediaTitle || '').replace(
                        'date',
                        getUserDate()
                    )} ${shareData.socialMediaMessage || ''}`.trim()}
                />
            ) : null}

            <ModalErrorV2
                show={errorOpen}
                setShowError={setErrorOpen}
                ModalErrorTitle="Could not share"
                ModalErrorDesc="Sorry, there was an error sharing. Please try again."
            />
        </section>
    );
}
